import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { createContext, useContext } from "react";
import { twJoin, twMerge } from "tailwind-merge";

import { Icon } from "src/base-components/Icon";

type SummaryVariant = "simple" | "info";

const SummaryContext = createContext<SummaryVariant>("simple");

type SummaryProps = {
  children?: React.ReactNode;
  variant: SummaryVariant;
  dataLoc?: string;
};

export const Summary: React.FC<SummaryProps> & {
  Cell: React.FC<CellProps>;
} = ({ children, variant, dataLoc }) => {
  return (
    <div
      className={twMerge(
        "flex divide-x divide-gray-200 border border-gray-200 bg-white",
        variant === "simple" && "rounded-lg",
      )}
      data-loc={dataLoc}
    >
      <SummaryContext.Provider value={variant}>
        {children}
      </SummaryContext.Provider>
    </div>
  );
};

type CellProps = {
  title: string;
  value?: number;
  icon?: IconProp;
  truncate?: boolean;
  children?: React.ReactNode;
  dataLoc?: string;
};

const Cell: React.FC<CellProps> = ({
  title,
  value,
  icon,
  truncate = true,
  dataLoc,
  children,
}) => {
  const variant = useContext(SummaryContext);

  const isInfo = variant === "info";
  const isSimple = variant === "simple";

  return (
    <div
      className="min-w-0 flex-1 gap-y-0.5 px-5 py-4 text-gray-800"
      data-loc={dataLoc}
    >
      {isSimple && (
        <h2
          className="font-inter-semibold-16px"
          data-loc={dataLoc && `${dataLoc}-value`}
        >
          {value}
        </h2>
      )}
      <span
        className={twMerge(
          "text-gray-800",
          isSimple && "font-inter-normal-13px",
          isInfo && "font-inter-semibold-13px",
        )}
        data-loc={dataLoc && `${dataLoc}-title`}
      >
        {title}
      </span>
      {isInfo && (
        <div
          className={twJoin(
            "flex items-center font-inter-medium-12px",
            truncate && "truncate",
          )}
          data-loc={dataLoc && `${dataLoc}-content`}
        >
          {icon && (
            <span className="mr-1" data-loc={dataLoc && `${dataLoc}-icon`}>
              <Icon color="text-gray-500" icon={icon} size="xs" />
            </span>
          )}
          {children}
        </div>
      )}
    </div>
  );
};

Summary.Cell = Cell;
